// Planted feet for the cowboys (James, 2026-10-23: "their feet sorta glide on the ground").
//
// Two parts:
// 1. gaitOf: at load, each looping locomotion clip is run through a small FK pass to find when each foot is on the
//    ground and how fast the planted foot slides back under the body. That gives the clip's true travel per cycle
//    (as a model-space vector, so strafes and backpedals get their own) and a per-frame contact curve per foot.
//    The view sets the stride cadence from the blended travel, so the clips themselves already roughly plant.
// 2. FootLock: what is left (blends between directions and speeds, starts, stops, turns, the stride warp, remote
//    players' smoothing) is taken out by locking each planted foot to the spot in the world where it came down and
//    bending the leg to reach it (two-bone IK, knee kept in the clip's own plane, the foot keeps its clip angle).
//    The swing foot follows the clip. Feet also stand on the ground under them (slopes, steps), and the hips drop
//    when a planted foot is out of reach. Allocation-free per frame.
import * as THREE from 'three';
import type { CowboyAsset, ClipData } from './asset';

export interface Gait {
  /** body travel per cycle in model space (m): the planted feet slide by minus this */
  tx: number; tz: number;
  /** per frame, per foot: 1 while the foot is planted */
  cL: Float32Array; cR: Float32Array;
}

const gaitCache = new WeakMap<ClipData, Gait | null>();

/** Analyse a looping clip's footfalls (cached per clip). Null when the rig has no feet. */
export function gaitOf(a: CowboyAsset, c: ClipData, footL: number, footR: number): Gait | null {
  const hit = gaitCache.get(c);
  if (hit !== undefined) return hit;
  let g: Gait | null = null;
  if (footL >= 0 && footR >= 0 && c.frames > 4) g = analyse(a, c, footL, footR);
  gaitCache.set(c, g);
  return g;
}

function analyse(a: CowboyAsset, c: ClipData, footL: number, footR: number): Gait {
  const nB = a.nB, F = c.frames;
  const wq = new Array(nB), wp = new Array(nB), done = new Uint8Array(nB);
  for (let i = 0; i < nB; i++) { wq[i] = new THREE.Quaternion(); wp[i] = new THREE.Vector3(); }
  const lq = new THREE.Quaternion(), lp = new THREE.Vector3();
  const pos = [new Float32Array(F * 3), new Float32Array(F * 3)];
  const solve = (f: number, i: number) => {
    if (done[i]) return;
    const b = a.bones[i];
    if (b.p >= 0) solve(f, b.p); // parent first: lq is shared scratch
    const o = (f * nB + i) * 4;
    lq.set(c.q[o] / 32767, c.q[o + 1] / 32767, c.q[o + 2] / 32767, c.q[o + 3] / 32767).normalize();
    if (b.p < 0) {
      wq[i].copy(lq);
      wp[i].set(c.hips[f * 3], c.hips[f * 3 + 1], c.hips[f * 3 + 2]);
    } else {
      lp.fromArray(b.pos).applyQuaternion(wq[b.p]);
      wp[i].copy(wp[b.p]).add(lp);
      wq[i].copy(wq[b.p]).multiply(lq);
    }
    done[i] = 1;
  };
  for (let f = 0; f < F; f++) {
    done.fill(0);
    solve(f, footL); solve(f, footR);
    pos[0][f * 3] = wp[footL].x; pos[0][f * 3 + 1] = wp[footL].y; pos[0][f * 3 + 2] = wp[footL].z;
    pos[1][f * 3] = wp[footR].x; pos[1][f * 3 + 1] = wp[footR].y; pos[1][f * 3 + 2] = wp[footR].z;
  }
  // planted = within 4 cm of the foot's lowest point in the cycle, and moving back under the body (not a hover in
  // the swing). The last frame repeats the first in a loop, so it is left out of the averages
  const fps = (F - 1) / Math.max(1e-3, c.dur);
  const cs = [new Float32Array(F), new Float32Array(F)];
  let vx = 0, vz = 0, n = 0;
  for (let k = 0; k < 2; k++) {
    const p = pos[k];
    let mn = 1e9;
    for (let f = 0; f < F - 1; f++) mn = Math.min(mn, p[f * 3 + 1]);
    for (let f = 0; f < F - 1; f++) cs[k][f] = p[f * 3 + 1] < mn + 0.04 ? 1 : 0;
    for (let f = 0; f < F - 1; f++) {
      const f1 = (f + 1) % (F - 1);
      if (!cs[k][f] || !cs[k][f1]) continue;
      vx += (p[f1 * 3] - p[f * 3]) * fps; vz += (p[f1 * 3 + 2] - p[f * 3 + 2]) * fps; n++;
    }
    cs[k][F - 1] = cs[k][0];
  }
  if (n) { vx /= n; vz /= n; }
  return { tx: -vx * c.dur, tz: -vz * c.dur, cL: cs[0], cR: cs[1] };
}

/** a gait's contact for one foot at phase ph (0..1), linearly interpolated between frames */
export function contactAt(g: Gait, foot: 0 | 1, ph: number) {
  const cv = foot ? g.cR : g.cL;
  const ft = ph * (cv.length - 1);
  const f0 = Math.floor(ft), f1 = Math.min(cv.length - 1, f0 + 1), k = ft - f0;
  return cv[f0] + (cv[f1] - cv[f0]) * k;
}

const _r = new THREE.Vector3(), _p = new THREE.Vector3(), _t = new THREE.Vector3(), _k = new THREE.Vector3(), _u = new THREE.Vector3();
const _fq = new THREE.Quaternion(), _h = new THREE.Vector3();

export type IK2 = (upper: THREE.Bone, lower: THREE.Bone, end: THREE.Bone, target: THREE.Vector3, pole: THREE.Vector3) => void;
export type SetWorldQuat = (bone: THREE.Bone, q: THREE.Quaternion, w: number) => void;

/** how far a planted foot may be left behind the clip's foot before it lets go and steps (m) */
const REACH = 0.42;
/** how long the hand-back to the clip takes when a planted foot lifts: a fifth of the stride cycle, so a running
 *  foot is clearly off the ground (a run has no double support), within these bounds (s) */
const LOCK_OUT_MIN = 0.05, LOCK_OUT_MAX = 0.18;
/** the least a foot keeps out from the hips' centre line when lanes are on (m) */
const LANE = 0.07;

class Foot {
  locked = false;
  armed = true;
  /** the hand-back after a lift: offset from the clip's foot, fading 1 -> 0 over the hand-back */
  fade = 0;
  rel = new THREE.Vector3();
  lock = new THREE.Vector3();
  off = 0; // smoothed ground offset under this foot
}

export class FootLock {
  private f = [new Foot(), new Foot()];
  private gate = 0;
  /** hips drop (m) so a planted foot downhill or left behind stays in reach; applied before the next solve */
  drop = 0;
  /** set by the view: the ground under a point (NaN when unknown) */
  ground: ((x: number, z: number, y: number) => number) | null = null;

  reset() { for (const ft of this.f) { ft.locked = false; ft.armed = true; ft.fade = 0; ft.off = 0; } this.gate = 0; this.drop = 0; }

  /**
   * Solve both legs. Bones are posed (FK) and their world matrices current. `on` is 0..1 (ground locomotion,
   * nothing overriding the legs), cycle the stride period (s), lanes 0..1 how firmly each foot keeps to its own side of the hips, cL / cR the blended clip contact per foot, y0 the cowboy's ground height.
   * Returns true if it touched the bones.
   */
  solve(dt: number, on: number, cycle: number, lanes: number, cL: number, cR: number, y0: number, fwdX: number, fwdZ: number,
    hips: THREE.Bone, up: [THREE.Bone, THREE.Bone], lo: [THREE.Bone, THREE.Bone], foot: [THREE.Bone, THREE.Bone],
    ik2: IK2, setWorldQuat: SetWorldQuat): boolean {
    this.gate += ((on > 0.5 ? 1 : 0) - this.gate) * Math.min(1, dt * (on > 0.5 ? 14 : 20));
    if (this.gate < 0.01) { this.reset(); return false; }
    const g = this.gate;
    // the hips drop from the last solve (a frame of lag; it is smoothed anyway)
    if (this.drop > 1e-4) { hips.position.y -= this.drop * g; hips.updateMatrixWorld(true); }
    let need = 0;
    hips.getWorldPosition(_h);
    const out = Math.min(LOCK_OUT_MAX, Math.max(LOCK_OUT_MIN, cycle * 0.2));
    for (let k = 0; k < 2; k++) {
      const ft = this.f[k], c = k ? cR : cL;
      foot[k].getWorldPosition(_p);
      _r.copy(_p); // where the clip (and the dropped hips) put the foot
      // feet in two lanes: a foot that crosses the hips' centre line is pushed back out to its own side
      // (right = fwd x up; the left foot is k 0)
      if (lanes > 1e-4) {
        const rx = -fwdZ, rz = fwdX, sd = k ? 1 : -1;
        const lat = ((_p.x - _h.x) * rx + (_p.z - _h.z) * rz) * sd; // + = out on its own side
        if (lat < LANE) { const push = (LANE - lat) * lanes * sd; _p.x += rx * push; _p.z += rz * push; }
        // and a swinging foot never passes the planted one (the body crossing over a planted foot is fine)
        const o = this.f[1 - k];
        if (!ft.locked && o.locked) {
          const sep = ((_p.x - o.lock.x) * rx + (_p.z - o.lock.z) * rz) * sd;
          if (sep < LANE * 2) { const push = (LANE * 2 - sep) * lanes * sd; _p.x += rx * push; _p.z += rz * push; }
        }
      }
      // the ground under the clip's foot (a slope or a step), smoothed so a ledge doesn't pop the foot
      let gy = this.ground ? this.ground(_p.x, _p.z, y0 + 0.5) : NaN;
      if (!(gy === gy) || Math.abs(gy - y0) > 0.45) gy = y0;
      ft.off += (gy - y0 - ft.off) * Math.min(1, dt * 18);
      // the foot's target is the clip's foot with the hips drop taken back out: the drop bends the knees, it never
      // sinks the feet
      _p.y += (ft.off + this.drop) * g;
      if (c < 0.4) ft.armed = true;
      if (!ft.locked && ft.armed && c > 0.55) {
        // plant where the foot is drawn right now (a foot still handing back from its last plant included), so the
        // plant itself never moves the foot
        ft.lock.copy(_p).addScaledVector(ft.rel, smooth01(ft.fade));
        ft.locked = true; ft.armed = false; ft.fade = 0;
      } else if (ft.locked) {
        const dx = _p.x - ft.lock.x, dz = _p.z - ft.lock.z;
        // lifted, or left too far behind (a warp or a respawn too): let go, and stay unlocked until the clip lifts this
        // foot. The hand-back
        // fades the offset from the clip's foot, in the body's frame, so the leg never chases a spot left behind
        if (c < 0.4 || dx * dx + dz * dz > REACH * REACH) {
          ft.locked = false; ft.rel.subVectors(ft.lock, _p); ft.rel.y = 0; ft.fade = 1;
        }
      }
      if (!ft.locked) ft.fade = Math.max(0, ft.fade - dt / out);
      // only the footprint is pinned: the height follows the clip (and the ground), so a heel strike still rolls flat
      if (ft.locked) { ft.lock.y = _p.y; _t.copy(ft.lock); } else _t.copy(_p).addScaledVector(ft.rel, smooth01(ft.fade));
      if (g < 1) _t.lerp(_p, 1 - g);
      // knee pole: the clip's own knee, pushed forward so a straight leg still bends the right way
      lo[k].getWorldPosition(_k);
      _k.x += fwdX * 0.35; _k.z += fwdZ * 0.35;
      // the foot keeps its clip angle (world), so it lands flat / rolls off the toe as animated
      foot[k].getWorldQuaternion(_fq);
      up[k].getWorldPosition(_u);
      const len = legLen(lo[k], foot[k]);
      need = Math.max(need, _u.distanceTo(_t) - len * 0.995);
      if (Math.abs(_t.x - _r.x) + Math.abs(_t.y - _r.y) + Math.abs(_t.z - _r.z) > 1e-4) {
        ik2(up[k], lo[k], foot[k], _t, _k);
        setWorldQuat(foot[k], _fq, 1);
      }
    }
    const want = Math.min(0.05, Math.max(0, need + this.drop));
    this.drop += (want - this.drop) * Math.min(1, dt * 10);
    return true;
  }
}

function legLen(lo: THREE.Bone, foot: THREE.Bone) { return lo.position.length() + foot.position.length(); }
function smooth01(x: number) { return x * x * (3 - 2 * x); }
